import { createMCPClient } from "@ai-sdk/mcp";
import { getAccessToken } from "@src/auth/auth-utils";
import { useGetSupportedAgents } from "@src/components/cards/prompt-card/queries/get-supported-agents";
import config from "@src/config";
import { DEFAULT_LLM_MODEL } from "@src/config/constants";
import { type ModelMessage, jsonSchema, tool } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	clearAgentSession,
	getAgentSession,
	setAgentSession,
} from "../agent-session";
import { buildAgentSystemPrompt } from "../agent-system-prompt";
import { createBoardTools, createChatTools } from "../agent-tools";
import { createAppTools } from "../app-tools";
import {
	AGENT_CHAT_MAX_TOOL_STEPS,
	filterBoardToolsForEditable,
} from "../board-agent-config";
import {
	AGENT_CHAT_MAX_MESSAGE_LENGTH,
	AGENT_CHAT_MIN_SEND_INTERVAL_MS,
} from "../constants";
import { listImageGenerationModels } from "../image-generation-models";
import { createLibraryTools } from "../library-tools";
import { createLiveBoardActionsProxy } from "../live-board-actions-proxy";
import {
	formatChoiceSelectionMessage,
	markChoiceResponded,
	validateChoiceSelection,
} from "../present-options";
import {
	continueAgentChatAfterApprovals,
	runAgentChatTurn,
} from "../run-agent-chat-turn";
import {
	cancelPendingApprovals,
	collectApprovalResponses,
	completeDeniedApprovalToolCalls,
	getPendingApprovals,
	hasPendingApprovals,
	markApprovalRespondedIncludingDuplicates,
	markApprovalsResponded,
} from "../tool-approvals";
import {
	collectToolCapableChatModels,
	readStoredChatLlmModel,
	resolveChatLlmModel,
	writeStoredChatLlmModel,
} from "../tool-capable-chat-models";
import type {
	AgentChatAttachment,
	AgentChatMessage,
	AgentChatStatus,
} from "../types";
import { useAppAgentContext } from "./app-agent-context";
import { useBoardAgentContext } from "./board-agent-context";
import { useLibraryAgentContext } from "./library-agent-context";

export { DEFAULT_LLM_MODEL, AGENT_CHAT_MAX_TOOL_STEPS };

const EMPTY_EXTERNAL_TOOLS: Record<string, unknown> = {};

type McpClient = Awaited<ReturnType<typeof createMCPClient>>;

export function useAgentChat() {
	const boardAgent = useBoardAgentContext();
	const libraryAgent = useLibraryAgentContext();
	const appAgent = useAppAgentContext();
	const {
		data: supportedAgentsData,
		isPending: areSupportedAgentsPending,
		isFetching: areSupportedAgentsFetching,
		isError: areSupportedAgentsError,
		refetch: refetchSupportedAgents,
	} = useGetSupportedAgents();
	const initialSession = getAgentSession();
	const [messages, setMessages] = useState<AgentChatMessage[]>(
		() => initialSession.messages,
	);
	const [status, setStatus] = useState<AgentChatStatus>(
		() => initialSession.status,
	);
	const [error, setError] = useState<string | null>(() => initialSession.error);
	const [llmModel, setLlmModelState] = useState(
		() =>
			initialSession.llmModel ?? readStoredChatLlmModel() ?? config.llmModel,
	);
	const [osTools, setOsTools] = useState(EMPTY_EXTERNAL_TOOLS);
	const [mcpTools, setMcpTools] = useState(EMPTY_EXTERNAL_TOOLS);
	const abortControllerRef = useRef<AbortController | null>(null);
	const mcpClientRef = useRef<McpClient | null>(null);
	const mcpLoadPromiseRef = useRef<Promise<void> | null>(null);
	const mcpToolsLoadedRef = useRef(false);
	const isMountedRef = useRef(true);
	const messagesRef = useRef(messages);
	const continuationMessagesRef = useRef<ModelMessage[] | null>(
		initialSession.continuationMessages,
	);
	const isSubmittingRef = useRef(false);
	const lastSendAtRef = useRef(0);

	messagesRef.current = messages;

	useEffect(() => {
		setAgentSession({
			messages,
			status,
			error,
			continuationMessages: continuationMessagesRef.current,
			llmModel,
		});
	}, [messages, status, error, llmModel]);

	const toolModels = useMemo(
		() => collectToolCapableChatModels(supportedAgentsData?.allAgents),
		[supportedAgentsData?.allAgents],
	);
	const resolvedLlmModel = useMemo(() => {
		if (toolModels.length === 0) {
			return llmModel;
		}

		return resolveChatLlmModel(toolModels, llmModel, config.llmModel);
	}, [llmModel, toolModels]);
	// Treat refetch-with-no-models as pending so Retry shows loading, not a
	// stuck failure/empty state.
	const areToolModelsPending =
		toolModels.length === 0 &&
		(areSupportedAgentsPending || areSupportedAgentsFetching);
	const toolModelsLoadFailed = !areToolModelsPending && areSupportedAgentsError;
	const areToolModelsReady =
		!areToolModelsPending &&
		!areSupportedAgentsError &&
		toolModels.length > 0 &&
		toolModels.some((model) => model.modelId === resolvedLlmModel);

	const retryToolModelsLoad = useCallback(() => {
		void refetchSupportedAgents();
	}, [refetchSupportedAgents]);

	useEffect(() => {
		if (!areToolModelsReady || resolvedLlmModel === llmModel) {
			return;
		}

		setLlmModelState(resolvedLlmModel);
		writeStoredChatLlmModel(resolvedLlmModel);
	}, [areToolModelsReady, llmModel, resolvedLlmModel]);

	const setLlmModel = useCallback(
		(modelId: string) => {
			if (
				toolModels.length > 0 &&
				!toolModels.some((model) => model.modelId === modelId)
			) {
				return;
			}

			setLlmModelState(modelId);
			writeStoredChatLlmModel(modelId);
			setAgentSession({ llmModel: modelId });
		},
		[toolModels],
	);

	const ensureMcpToolsLoaded = useCallback(() => {
		const mcpUrl = config.mcpHttpUrl;
		if (!mcpUrl || mcpToolsLoadedRef.current || mcpLoadPromiseRef.current) {
			return;
		}

		mcpLoadPromiseRef.current = (async () => {
			let mcpClient: McpClient | null = null;
			try {
				const token = await getAccessToken();
				if (!isMountedRef.current) {
					return;
				}

				const headers: Record<string, string> = {};
				if (token) {
					headers.Authorization = `Bearer ${token}`;
				}

				mcpClient = await createMCPClient({
					transport: {
						type: "http",
						url: mcpUrl,
						headers,
						fetch: (url, options) => window.fetch(url, options),
					},
				});

				if (!isMountedRef.current) {
					await mcpClient.close();
					return;
				}

				mcpClientRef.current = mcpClient;
				const tools = await mcpClient.tools();

				if (!isMountedRef.current) {
					mcpClientRef.current = null;
					await mcpClient.close();
					return;
				}

				setMcpTools(tools);
				mcpToolsLoadedRef.current = true;
			} catch (err) {
				if (mcpClient && mcpClientRef.current === mcpClient) {
					mcpClientRef.current = null;
				}
				if (mcpClient) {
					await mcpClient.close().catch(() => undefined);
				}
				if (isMountedRef.current) {
					console.error("[useAgentChat] Failed to load MCP tools:", err);
				}
			} finally {
				mcpLoadPromiseRef.current = null;
			}
		})();
	}, []);

	useEffect(() => {
		const loadOsTools = async () => {
			const electronAPI = window.electronAPI;
			if (!electronAPI || !electronAPI.isElectron) {
				return;
			}

			try {
				const settings = await electronAPI.getCliSettings();
				if (!settings?.hasConsentedToCli) {
					return;
				}

				const response = await electronAPI.listOsTools();
				if (!response || !Array.isArray(response.tools)) {
					return;
				}

				const toolsRecord: Record<string, unknown> = {};
				for (const t of response.tools) {
					if (!t.name.startsWith("os_")) {
						continue;
					}

					toolsRecord[t.name] = tool({
						description: t.description,
						inputSchema: jsonSchema(t.inputSchema),
						execute: async (args) => {
							return await electronAPI.executeOsTool(
								t.name,
								args as Record<string, unknown>,
							);
						},
					});
				}
				setOsTools(toolsRecord);
			} catch (err) {
				console.error(
					"[useAgentChat] Failed to load OS tools from Electron:",
					err,
				);
			}
		};

		void loadOsTools();
	}, []);

	useEffect(() => {
		isMountedRef.current = true;

		return () => {
			isMountedRef.current = false;
			abortControllerRef.current?.abort();
			const mcpClient = mcpClientRef.current;
			mcpClientRef.current = null;
			mcpLoadPromiseRef.current = null;
			mcpToolsLoadedRef.current = false;
			if (mcpClient) {
				void mcpClient.close();
			}
		};
	}, []);

	const abortStream = useCallback(() => {
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
		isSubmittingRef.current = false;

		if (status === "awaiting_approval") {
			const cancelled = cancelPendingApprovals(messagesRef.current);
			messagesRef.current = cancelled;
			setMessages(cancelled);
			continuationMessagesRef.current = null;
			setStatus("idle");
			return;
		}

		setStatus((current) =>
			current === "submitting" || current === "awaiting_choice"
				? "idle"
				: current,
		);
	}, [status]);

	const clearChat = useCallback(() => {
		abortStream();
		setMessages([]);
		setStatus("idle");
		setError(null);
		continuationMessagesRef.current = null;
		clearAgentSession();
	}, [abortStream]);

	const dismissError = useCallback(() => {
		setError(null);
		setStatus((current) => (current === "error" ? "idle" : current));
	}, []);

	const imageModelsRef = useRef(
		listImageGenerationModels(undefined, undefined),
	);
	imageModelsRef.current = listImageGenerationModels(
		supportedAgentsData?.allAgents,
		supportedAgentsData?.myAgents,
	);

	const selectionGateRef = useRef({ presentOptionsCalled: false });
	const hasBoardTools = Boolean(boardAgent?.actions);
	const skillAvailability = useMemo(
		() => ({
			hasBoardTools,
			hasLibraryTools: Boolean(libraryAgent?.actions),
			hasAppTools: Boolean(appAgent?.actions),
		}),
		[appAgent?.actions, hasBoardTools, libraryAgent?.actions],
	);

	const chatTools = useMemo(
		() =>
			createChatTools({
				getImageModels: () => imageModelsRef.current,
				getMessages: () => messagesRef.current,
				selectionGate: selectionGateRef.current,
				skillAvailability,
			}),
		[skillAvailability],
	);

	const liveBoardActions = useMemo(() => createLiveBoardActionsProxy(), []);

	const boardTools = useMemo(() => {
		if (!boardAgent?.actions) {
			return undefined;
		}

		const tools = createBoardTools({
			actions: liveBoardActions,
			getMessages: () => messagesRef.current,
		});

		return filterBoardToolsForEditable(tools, {
			editable: boardAgent.snapshot?.editable,
		});
	}, [boardAgent?.actions, boardAgent?.snapshot?.editable, liveBoardActions]);

	const libraryTools = useMemo(() => {
		if (!libraryAgent?.actions) {
			return undefined;
		}

		return createLibraryTools({
			actions: libraryAgent.actions,
		});
	}, [libraryAgent?.actions]);

	const appTools = useMemo(() => {
		if (!appAgent?.actions) {
			return undefined;
		}

		return createAppTools({
			actions: appAgent.actions,
		});
	}, [appAgent?.actions]);

	const agentTools = useMemo(() => {
		return {
			...chatTools,
			...(boardTools ?? {}),
			...(libraryTools ?? {}),
			...(appTools ?? {}),
			...osTools,
			...mcpTools,
		};
	}, [appTools, boardTools, chatTools, libraryTools, mcpTools, osTools]);

	const systemPrompt = useMemo(
		() => buildAgentSystemPrompt(skillAvailability),
		[skillAvailability],
	);

	const applyTurnResult = useCallback(
		(result: Awaited<ReturnType<typeof runAgentChatTurn>>) => {
			if (result.aborted) {
				continuationMessagesRef.current = null;
				setStatus("idle");
				return;
			}

			if (!result.ok) {
				continuationMessagesRef.current = null;
				setError(result.error);
				setStatus("error");
				return;
			}

			setMessages(result.messages);
			continuationMessagesRef.current = result.awaitingApproval
				? (result.continuationMessages ?? null)
				: null;

			if (result.awaitingApproval) {
				setStatus("awaiting_approval");
			} else if (result.awaitingChoice) {
				setStatus("awaiting_choice");
			} else {
				setStatus("idle");
			}

			if (result.touchedBoard) {
				boardAgent?.focusSessionTouchedShapes?.(result.touchedShapeIds);
			}
		},
		[boardAgent],
	);

	const sendMessage = useCallback(
		async (
			text: string,
			options?: {
				baseMessages?: AgentChatMessage[];
				attachments?: AgentChatAttachment[];
			},
		): Promise<boolean> => {
			const trimmed = text.trim();
			const attachments = options?.attachments;
			const hasAttachments = (attachments?.length ?? 0) > 0;

			if ((!trimmed && !hasAttachments) || isSubmittingRef.current) {
				return false;
			}

			if (!areToolModelsReady) {
				return false;
			}

			// Suggestion chips / send can fire without a textarea focus event.
			ensureMcpToolsLoaded();

			if (trimmed.length > AGENT_CHAT_MAX_MESSAGE_LENGTH) {
				setError(
					`Message is too long (max ${AGENT_CHAT_MAX_MESSAGE_LENGTH.toLocaleString()} characters)`,
				);
				setStatus("error");
				return false;
			}

			const now = Date.now();
			if (now - lastSendAtRef.current < AGENT_CHAT_MIN_SEND_INTERVAL_MS) {
				setError("Please wait a moment before sending another message");
				setStatus("error");
				return false;
			}
			lastSendAtRef.current = now;

			abortControllerRef.current?.abort();
			const abortController = new AbortController();
			abortControllerRef.current = abortController;

			isSubmittingRef.current = true;
			setStatus("submitting");
			setError(null);
			continuationMessagesRef.current = null;
			selectionGateRef.current.presentOptionsCalled = false;

			try {
				const result = await runAgentChatTurn({
					text: trimmed,
					attachments,
					baseMessages: options?.baseMessages ?? messagesRef.current,
					llmModel: resolvedLlmModel,
					agentTools,
					system: systemPrompt,
					skillAvailability,
					abortSignal: abortController.signal,
					onMessagesChange: setMessages,
					onToolExecutionEnd: () => {
						boardAgent?.refreshSnapshot?.();
					},
				});

				applyTurnResult(result);
			} catch (caught) {
				const message =
					caught instanceof Error
						? caught.message
						: "Something went wrong. Please try again.";
				setError(message);
				setStatus("error");
			} finally {
				isSubmittingRef.current = false;
				if (abortControllerRef.current === abortController) {
					abortControllerRef.current = null;
				}
			}

			return true;
		},
		[
			agentTools,
			applyTurnResult,
			areToolModelsReady,
			boardAgent,
			ensureMcpToolsLoaded,
			resolvedLlmModel,
			skillAvailability,
			systemPrompt,
		],
	);

	const continueAfterApprovals = useCallback(
		async (nextMessages: AgentChatMessage[]) => {
			const continuation = continuationMessagesRef.current;
			const approvals = collectApprovalResponses(nextMessages);
			if (!continuation || approvals.length === 0) {
				setError("No pending tool approval to continue");
				setStatus("error");
				return;
			}

			abortControllerRef.current?.abort();
			const abortController = new AbortController();
			abortControllerRef.current = abortController;

			isSubmittingRef.current = true;
			setStatus("submitting");
			setError(null);
			selectionGateRef.current.presentOptionsCalled = false;

			try {
				const result = await continueAgentChatAfterApprovals({
					messages: nextMessages,
					continuationMessages: continuation,
					approvals,
					llmModel: resolvedLlmModel,
					agentTools,
					system: systemPrompt,
					skillAvailability,
					abortSignal: abortController.signal,
					onMessagesChange: setMessages,
					onToolExecutionEnd: () => {
						boardAgent?.refreshSnapshot?.();
					},
				});

				applyTurnResult(result);
			} catch (caught) {
				const message =
					caught instanceof Error
						? caught.message
						: "Something went wrong. Please try again.";
				setError(message);
				setStatus("error");
			} finally {
				isSubmittingRef.current = false;
				if (abortControllerRef.current === abortController) {
					abortControllerRef.current = null;
				}
			}
		},
		[
			agentTools,
			applyTurnResult,
			boardAgent,
			resolvedLlmModel,
			skillAvailability,
			systemPrompt,
		],
	);

	const respondToChoice = useCallback(
		(choiceId: string, optionId: string, _label: string) => {
			if (isSubmittingRef.current) {
				return;
			}

			const validated = validateChoiceSelection(
				messagesRef.current,
				choiceId,
				optionId,
			);
			if (!validated) {
				setError("Invalid option selected");
				setStatus("error");
				return;
			}

			const withResponse = markChoiceResponded(
				messagesRef.current,
				choiceId,
				optionId,
				validated.label,
			);

			// Sync ref before sendMessage so tool gates (e.g. generateImageInChat
			// getPendingChoice) see the choice-response without waiting on render.
			messagesRef.current = withResponse;
			setMessages(withResponse);
			void sendMessage(formatChoiceSelectionMessage(validated.label), {
				baseMessages: withResponse,
			});
		},
		[sendMessage],
	);

	const respondToApproval = useCallback(
		(approvalId: string, approved: boolean) => {
			if (isSubmittingRef.current || status !== "awaiting_approval") {
				return;
			}

			const pending = getPendingApprovals(messagesRef.current);
			if (!pending.some((part) => part.approvalId === approvalId)) {
				setError("Invalid approval selection");
				setStatus("error");
				return;
			}

			const withResponse = completeDeniedApprovalToolCalls(
				markApprovalRespondedIncludingDuplicates(
					messagesRef.current,
					approvalId,
					approved,
				),
			);
			messagesRef.current = withResponse;
			setMessages(withResponse);

			if (hasPendingApprovals(withResponse)) {
				return;
			}

			void continueAfterApprovals(withResponse);
		},
		[continueAfterApprovals, status],
	);

	const respondToAllApprovals = useCallback(
		(approved: boolean) => {
			if (isSubmittingRef.current || status !== "awaiting_approval") {
				return;
			}

			const pending = getPendingApprovals(messagesRef.current);
			if (pending.length === 0) {
				return;
			}

			const withResponses = completeDeniedApprovalToolCalls(
				markApprovalsResponded(
					messagesRef.current,
					pending.map((part) => ({
						approvalId: part.approvalId,
						approved,
					})),
				),
			);
			messagesRef.current = withResponses;
			setMessages(withResponses);
			void continueAfterApprovals(withResponses);
		},
		[continueAfterApprovals, status],
	);

	const isAwaitingChoice = status === "awaiting_choice";
	const isAwaitingApproval = status === "awaiting_approval";

	return useMemo(
		() => ({
			messages,
			status,
			error,
			sendMessage,
			clearChat,
			abortStream,
			dismissError,
			respondToChoice,
			respondToApproval,
			respondToAllApprovals,
			llmModel,
			setLlmModel,
			toolModels,
			areToolModelsPending,
			toolModelsLoadFailed,
			areToolModelsReady,
			retryToolModelsLoad,
			ensureMcpToolsLoaded,
			isSubmitting: status === "submitting",
			isAwaitingChoice,
			isAwaitingApproval,
		}),
		[
			messages,
			status,
			error,
			sendMessage,
			clearChat,
			abortStream,
			dismissError,
			respondToChoice,
			respondToApproval,
			respondToAllApprovals,
			llmModel,
			setLlmModel,
			toolModels,
			areToolModelsPending,
			toolModelsLoadFailed,
			areToolModelsReady,
			retryToolModelsLoad,
			ensureMcpToolsLoaded,
			isAwaitingChoice,
			isAwaitingApproval,
		],
	);
}
